import { useCallback, useEffect } from "react";
import api from "../axios/axiosConfig";

declare global {
  interface Window {
    Iugu?: any;
    Stripe?: any;
  }
}

type CardPayload = {
  number: string;
  verification_value: string;
  first_name: string;
  last_name: string;
  month: string;
  year: string;
};

export type PaymentTokenResult = {
  provider: "stripe" | "iugu" | "pagarme";
  raw: any;
  stripeError?: any;
  // Chave usada para tokenizar. Precisa ser reaproveitada nas etapas
  // seguintes (createPaymentMethod e confirmCardSetup), senao o SetupIntent
  // criado pelo backend cai em outra conta/modo e a Stripe responde
  // "No such setup_intent".
  publishableKey?: string;
  // Gateway que efetivamente tokenizou. Vai como pgw_id no POST
  // /UserPaymentMethods para o backend nao re-resolver por conta propria.
  pgwId?: string;
};

type PaymentProviderConfig = {
  stripePublishableKey?: string;
  iuguAccountId?: string;
  iuguTestMode?: string;
};

const STRIPE_V2_SCRIPT_ID = "stripe-v2-script";
const STRIPE_V3_SCRIPT_ID = "stripe-v3-script";
const IUGU_SCRIPT_ID = "iugu-script";
let stripeV2Instance: any | null = null;

const loadScript = (id: string, src: string) => {
  const existingScript = document.getElementById(
    id
  ) as HTMLScriptElement | null;
  if (existingScript) {
    return new Promise<void>((resolve, reject) => {
      if (existingScript.getAttribute("data-loaded") === "true") {
        resolve();
        return;
      }

      existingScript.addEventListener("load", () => resolve());
      existingScript.addEventListener("error", () => reject());
    });
  }

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.id = id;
    script.async = true;
    script.onload = () => {
      script.setAttribute("data-loaded", "true");
      resolve();
    };
    script.onerror = () => reject();

    document.head.appendChild(script);
  });
};

const normalizeYear = (year: string) => {
  if (!year) return year;

  return year.length === 2 ? `20${year}` : year;
};

const isEloCard = (cardNumber: string) => {
  const numbersOnly = cardNumber.replace(/\D/g, "");
  const eloRegex =
    /^(40117[89]|431274|438935|451416|45763[12]|457393|504175|627780|636297|636368|6500(3[1-3]|[4-9][0-9])|650[1-5][0-9]{3}|6509[0-9]{3}|6516[5-9][0-9]{2}|6550[0-9]{3})/;

  return eloRegex.test(numbersOnly);
};

type BackendProvider = "STRIPE" | "IUGU" | "PAGARME";

const normalizeProvider = (raw: any): BackendProvider => {
  const value = String(raw || "")
    .trim()
    .toUpperCase();

  if (value === "STRIPE") return "STRIPE";
  if (value === "PAGARME") return "PAGARME";

  return "IUGU";
};

// Ate 8 digitos: o /current casa a faixa de BIN com 6 a 8, e faixas de 8
// digitos so casam se o BIN vier inteiro.
const getBin = (cardNumber: string): string | undefined => {
  const digits = (cardNumber || "").replace(/\D/g, "");
  return digits.length >= 6 ? digits.substring(0, 8) : undefined;
};

/**
 * A API que resolve o gateway pelo bin devolve as credenciais do provedor em
 * creditCardCredentials. Para a Stripe, a chave publicavel vem em
 * creditCardCredentials.publishable_key.
 *
 * Usar essa chave (e nao a do .env) e o que garante que a tokenizacao aconteca
 * na mesma conta e no mesmo modo (test/live) da secret key que o backend usa
 * para criar o SetupIntent. Chaves de contas diferentes fazem a Stripe
 * responder "No such setup_intent" no confirmCardSetup.
 */
const extractPublishableKey = (data: any): string | undefined => {
  const value = data?.creditCardCredentials?.publishable_key;

  return typeof value === "string" && value.trim() ? value.trim() : undefined;
};

/**
 * Pagar.me: a chave publica (pk_...) vem em creditCardCredentials.public_key,
 * da mesma linha de gateway que o backend usa para salvar o cartao. Tokenizar
 * com a chave de outra conta gera um token que o backend nao consegue usar.
 */
const extractPublicKey = (data: any): string | undefined => {
  const value = data?.creditCardCredentials?.public_key;

  return typeof value === "string" && value.trim() ? value.trim() : undefined;
};

/**
 * Id do gateway resolvido pelo backend a partir do bin. O DTO do backend
 * (ApplicationPaymentProvidersResponse) documenta que ele deve ser devolvido
 * como pgw_id no cadastro do cartao: assim o backend usa o mesmo gateway que
 * tokenizou, em vez de re-resolver por conta propria e correr o risco de
 * divergir (regra de bin alterada ou gateway desabilitado no meio do fluxo).
 */
const extractPgwId = (data: any): string | undefined => {
  const value = data?.creditCardPgwId;

  return typeof value === "string" && value.trim() ? value.trim() : undefined;
};

type CreditCardProviderInfo = {
  provider: BackendProvider;
  publishableKey?: string;
  publicKey?: string;
  pgwId?: string;
};

const fetchCreditCardProvider = async (
  bin?: string
): Promise<CreditCardProviderInfo> => {
  const url = bin
    ? `/payment/paymentprovider/current?bin=${encodeURIComponent(bin)}`
    : `/payment/paymentprovider/current`;

  const resp = await api.get(url);
  const data = resp.data;

  return {
    provider: normalizeProvider(data?.creditCardProvider),
    publishableKey: extractPublishableKey(data),
    publicKey: extractPublicKey(data),
    pgwId: extractPgwId(data),
  };
};

const loadStripeV2 = async (publishableKey: string) => {
  await loadScript(STRIPE_V2_SCRIPT_ID, "https://js.stripe.com/v2/");

  if (!stripeV2Instance) {
    stripeV2Instance = window.Stripe;
  }

  if (!stripeV2Instance) {
    throw new Error("Stripe v2 script has not been loaded yet.");
  }

  stripeV2Instance.setPublishableKey(publishableKey);
  return stripeV2Instance;
};

const loadStripeV3 = async (publishableKey: string) => {
  await loadScript(STRIPE_V3_SCRIPT_ID, "https://js.stripe.com/v3/");

  if (!window.Stripe) {
    throw new Error("Stripe v3 script has not been loaded yet.");
  }

  return window.Stripe(publishableKey);
};

const createStripeToken = (publishableKey: string, card: CardPayload) => {
  return new Promise<any>(async (resolve, reject) => {
    let previousStripeGlobal: any;

    try {
      const stripe = await loadStripeV2(publishableKey);
      previousStripeGlobal = window.Stripe;
      window.Stripe = stripe;

      stripe.card.createToken(
        {
          number: card.number,
          cvc: card.verification_value,
          exp_month: Number(card.month),
          exp_year: Number(normalizeYear(card.year)),
          name: `${card.first_name} ${card.last_name}`.trim(),
        },
        (status: number, response: any) => {
          if (typeof previousStripeGlobal !== "undefined") {
            window.Stripe = previousStripeGlobal;
          }

          if (response?.error) {
            reject(response.error);
            return;
          }

          resolve(response);
        }
      );
    } catch (error) {
      if (typeof previousStripeGlobal !== "undefined") {
        window.Stripe = previousStripeGlobal;
      }

      reject(error);
    }
  });
};

const createIuguToken = async (
  accountId: string,
  testMode: string,
  card: CardPayload,
  throwOnError = false
) => {
  await loadScript(IUGU_SCRIPT_ID, "https://js.iugu.com/v2");

  if (!window.Iugu) {
    throw new Error("Iugu script has not been loaded yet.");
  }

  window.Iugu.setAccountID(accountId);
  window.Iugu.setTestMode(testMode.toLowerCase() === "true");

  return new Promise((resolve, reject) => {
    window.Iugu.createPaymentToken(card, (response: any) => {
      if (response?.errors && throwOnError) {
        reject(response);
        return;
      }

      resolve(response);
    });
  });
};

const PAGARME_TOKENS_URL = "https://api.pagar.me/core/v5/tokens";

/**
 * Tokeniza o cartao direto na Pagar.me (API Core v5), com a chave publica. O
 * numero do cartao nunca passa pelo nosso backend: ele so recebe o token_...,
 * que vale por alguns segundos e e trocado por um card_... no cadastro.
 *
 * A chamada sai do navegador (CORS liberado pela Pagar.me: preflight e POST
 * com Origin de appdev.spott.eco e webapp.spott.eco responderam 200 com a
 * chave de teste em 02/10/2026).
 */
const createPagarmeToken = async (publicKey: string, card: CardPayload) => {
  const response = await fetch(
    `${PAGARME_TOKENS_URL}?appId=${encodeURIComponent(publicKey)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "card",
        card: {
          number: card.number,
          holder_name: `${card.first_name} ${card.last_name}`.trim(),
          exp_month: Number(card.month),
          exp_year: Number(normalizeYear(card.year)),
          cvv: card.verification_value,
        },
      }),
    }
  );

  const body = await response.json().catch(() => undefined);

  if (!response.ok || !body?.id) {
    // 422 traz o motivo por campo; o resto e erro de chave/dominio, que nao
    // ajuda o usuario. As duas viram a mesma mensagem de dado invalido.
    console.warn("[pagamento] Pagar.me recusou a tokenizacao", response.status, body);
    throw new Error(
      "Não foi possível validar os dados do cartão. Confira o número, a validade e o CVV."
    );
  }

  return body;
};

const createPaymentMethodFromToken = async (
  publishableKey: string,
  token: string,
  cardholderName?: string
) => {
  const stripe = await loadStripeV3(publishableKey);

  const result = await stripe.createPaymentMethod({
    type: 'card',
    card: {
      token: token,
    },
    billing_details: {
      name: cardholderName?.trim(),
    },
  });

  if (result.error) {
    throw result.error;
  }

  return result.paymentMethod;
};

export default function usePaymentProvider({
  stripePublishableKey,
  iuguAccountId,
  iuguTestMode = "false",
}: PaymentProviderConfig) {
  useEffect(() => {
    if (!stripePublishableKey) return;

    loadStripeV2(stripePublishableKey).catch(() => undefined);
  }, [stripePublishableKey]);

  const createPaymentToken = useCallback(
    async (card: CardPayload): Promise<PaymentTokenResult> => {
      const sanitizedCard = {
        ...card,
        number: card.number.replace(/\D/g, ""),
      };

      const bin = getBin(sanitizedCard.number);

      // 1) Decide provider pelo backend (bin opcional)
      let provider: BackendProvider;
      let apiPublishableKey: string | undefined;
      let apiPublicKey: string | undefined;
      let apiPgwId: string | undefined;

      try {
        const providerInfo = await fetchCreditCardProvider(bin);
        provider = providerInfo.provider;
        apiPublishableKey = providerInfo.publishableKey;
        apiPublicKey = providerInfo.publicKey;
        apiPgwId = providerInfo.pgwId;
      } catch (err) {
        // Fallback seguro: mantém o comportamento atual caso a API falhe
        // (tenta Stripe e se for Elo tenta IUGU).
        let stripeError: any;

        try {
          if (!stripePublishableKey) {
            throw new Error("Stripe publishable key not configured.");
          }

          const raw = await createStripeToken(
            stripePublishableKey,
            sanitizedCard
          );
          return {
            provider: "stripe",
            raw,
            publishableKey: stripePublishableKey,
          };
        } catch (error) {
          stripeError = error;
        }

        if (isEloCard(sanitizedCard.number)) {
          if (!iuguAccountId) {
            throw stripeError;
          }

          const raw = await createIuguToken(
            iuguAccountId || "",
            iuguTestMode,
            sanitizedCard
          );

          return { provider: "iugu", raw, stripeError };
        }

        throw stripeError;
      }

      // 2) Executa fluxo conforme provider retornado
      if (provider === "STRIPE") {
        // A chave da API tem precedencia: ela acompanha a conta e o modo do
        // backend, que e quem cria o SetupIntent. A do .env e so fallback.
        const resolvedKey = apiPublishableKey || stripePublishableKey;

        if (!resolvedKey) {
          throw new Error("Stripe publishable key not configured.");
        }

        if (!apiPublishableKey) {
          console.warn(
            "[pagamento] a API de gateway nao retornou a chave publicavel; " +
              "usando a do .env. Se a conta/modo do backend for diferente, o " +
              "confirmCardSetup vai falhar com 'No such setup_intent'."
          );
        }

        const raw = await createStripeToken(resolvedKey, sanitizedCard);
        return {
          provider: "stripe",
          raw,
          publishableKey: resolvedKey,
          pgwId: apiPgwId,
        };
      }

      if (provider === "PAGARME") {
        // Sem fallback no .env: a chave e de uma conta especifica, e so a da
        // API garante que o token sai da mesma conta que vai salvar o cartao.
        if (!apiPublicKey) {
          throw new Error("Pagar.me public key not returned by the API.");
        }

        const raw = await createPagarmeToken(apiPublicKey, sanitizedCard);
        return { provider: "pagarme", raw, pgwId: apiPgwId };
      }

      // IUGU
      if (!iuguAccountId) {
        throw new Error("IUGU account id not configured.");
      }

      const raw = await createIuguToken(
        iuguAccountId || "",
        iuguTestMode,
        sanitizedCard
      );

      return { provider: "iugu", raw, pgwId: apiPgwId };
    },
    [iuguAccountId, iuguTestMode, stripePublishableKey]
  );

  const confirmStripeCardSetup = useCallback(
    async (
      clientSecret: string,
      paymentMethodId: string,
      publishableKeyOverride?: string
    ) => {
      const resolvedKey = publishableKeyOverride || stripePublishableKey;

      if (!resolvedKey) {
        throw new Error("Stripe publishable key not configured.");
      }

      const stripe = await loadStripeV3(resolvedKey);

      return stripe.confirmCardSetup(clientSecret, {
        payment_method: paymentMethodId,
      });
    },
    [stripePublishableKey]
  );

  const createPaymentMethodFromTokenWrapper = useCallback(
    async (
      token: string,
      cardholderName?: string,
      publishableKeyOverride?: string
    ) => {
      const resolvedKey = publishableKeyOverride || stripePublishableKey;

      if (!resolvedKey) {
        throw new Error("Stripe publishable key not configured.");
      }

      return createPaymentMethodFromToken(resolvedKey, token, cardholderName);
    },
    [stripePublishableKey]
  );

  return {
    createPaymentToken,
    confirmStripeCardSetup,
    createPaymentMethodFromToken: createPaymentMethodFromTokenWrapper,
  };
}
